import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { WebBuilderPreloader } from '@/components/webbuilder/ui/loaders/WebBuilderPreloader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isStoreKycApprovedForPublishing } from '@/utils/storeKyc';
import {
  authorizeStoreRequest,
  checkAuthStatusRequest,
  setCurrentStore,
} from '@/store/slices/auth.slice';
import { whitelabelConfig } from '@/config/whitelabel';

const configuredStoreDashboardPath = `/${whitelabelConfig.storeType}/${whitelabelConfig.storeId}/dashboard`;

export function StoreGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const { statusLoading, isAuthenticated, verifiedStores } = useAuthStatus();
  const { currentStoreInfo, statusChecked, isLoading, authPhase, error } = useSelector((state: RootState) => state.auth);
  const [validating, setValidating] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showKycRedirectModal, setShowKycRedirectModal] = useState(false);
  const [pendingKycRedirectPath, setPendingKycRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    setValidating(true);
    setHasAccess(false);
    setShowKycRedirectModal(false);
    setPendingKycRedirectPath(null);
  }, [storeType, storeId, location.pathname]);

  useEffect(() => {
    async function checkAccess() {
      if (!statusChecked && !statusLoading) {
        dispatch(checkAuthStatusRequest({ trigger: 'route-guard' }));
        return;
      }

      if (statusLoading || !statusChecked) return;

      if (!isAuthenticated) {
        navigate('/auth/login', { replace: true });
        return;
      }

      if (!storeType || !storeId) {
        navigate(configuredStoreDashboardPath, { replace: true });
        return;
      }

      if (storeType !== whitelabelConfig.storeType) {
        navigate(configuredStoreDashboardPath, { replace: true });
        return;
      }

      if (storeId !== whitelabelConfig.storeId) {
        navigate(configuredStoreDashboardPath, { replace: true });
        return;
      }

      let store = verifiedStores.find(
        (s) => s.storeId === storeId && s.storeType === storeType
      );

      // If not found in verifiedStores, try falling back to currentStoreInfo.
      if (!store) {
        if (
          currentStoreInfo &&
          currentStoreInfo.storeId === storeId &&
          currentStoreInfo.storeType === storeType &&
          currentStoreInfo.authorized === true
        ) {
          store = currentStoreInfo;
        } else {
          navigate('/auth/login', { replace: true });
          return;
        }
      }

      if (store.authorized !== true) {
        if (authPhase === 'error' && error) {
          navigate('/auth/login', { replace: true });
          return;
        }

        if (authPhase !== 'authorizingStore' && !isLoading) {
          dispatch(authorizeStoreRequest(store));
        }
        return;
      }

      if (
        !currentStoreInfo ||
        currentStoreInfo.storeId !== store.storeId ||
        currentStoreInfo.storeType !== store.storeType ||
        currentStoreInfo.authorized !== true
      ) {
        dispatch(setCurrentStore({ ...store, authorized: true }));
      }

      // Check onboarding status using the effective `store` object
      const needsKycApproval = !(
        store.authorized === true && isStoreKycApprovedForPublishing(store)
      );

      if (
        needsKycApproval &&
        location.pathname.includes('/webbuilder') &&
        !location.pathname.includes('/settings')
      ) {
        setPendingKycRedirectPath(`/${storeType}/${storeId}/settings?tab=business&kycRequired=webbuilder`);
        setShowKycRedirectModal(true);
        setValidating(false);
        return;
      }

      setHasAccess(true);
      setValidating(false);
    }

    checkAccess();
  }, [statusChecked, statusLoading, isAuthenticated, verifiedStores, currentStoreInfo, storeType, storeId, navigate, location, dispatch, isLoading, authPhase, error]);

  if (statusLoading || !statusChecked || isLoading || validating)
    return (
      <WebBuilderPreloader
        message={statusLoading ? "Loading page..." : "Validating access..."}
      />
    );
  if (!hasAccess && !showKycRedirectModal) return null;

  return (
    <>
      {hasAccess ? children : null}

      <Dialog open={showKycRedirectModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>KYC Approval Required</DialogTitle>
            <DialogDescription>
              Web Builder is available only after your KYC is approved. You will now be redirected to
              Business Information so you can complete verification.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowKycRedirectModal(false);
                if (storeType && storeId) {
                  navigate(`/${storeType}/${storeId}/dashboard`, { replace: true });
                } else {
                  navigate(configuredStoreDashboardPath, { replace: true });
                }
              }}
            >
              Not now
            </Button>
            <Button
              onClick={() => {
                setShowKycRedirectModal(false);
                navigate(
                  pendingKycRedirectPath || `/${storeType}/${storeId}/settings?tab=business&kycRequired=webbuilder`,
                  { replace: true },
                );
              }}
            >
              Go to Business Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
